import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Card, Pagination, Skeleton, SortableHeader, Table, TableColumnsMenu, cn, useTableColumns, } from '@wit-support/ui';
import { paybackApi } from '../api.js';
import { accountTitle } from '../campaign-name.js';
import { intRu, num2, purchasesRu, thetaOf } from '../format.js';
import { columnHint, hintOf } from '../glossary.js';
import { markerValueLabel } from '../marker-labels.js';
import { markerValues, MIN_N, paginate } from '../portrait-logic.js';
import { PAYBACK_STALE_MS, reportKey, retryOnce } from '../queries.js';
import { sortBy } from '../table-sort.js';
import { menuSort, paybackColumnsKey } from '../table-columns.js';
import { useTableUrl } from '../url-state.js';
import { dotTone } from './marker-card.js';
const TONE_TEXT = {
    good: 'text-[var(--color-state-success-fg)]',
    mid: '',
    bad: 'text-[var(--color-state-danger-fg)]',
    none: 'text-[var(--color-text-muted)]',
};
function ValueCell({ g }) {
    if (!g)
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    const faint = g.purchases < MIN_N;
    const v = g.answer.roas3;
    return (_jsxs("span", { className: cn('whitespace-nowrap', faint && 'opacity-45'), title: `${v === null ? 'нет оценки' : `вернётся ${num2(v)} $ на 1 $`} · ${purchasesRu(g.purchases)}${faint ? ` · меньше ${MIN_N} — ненадёжно` : ''}`, children: [_jsx("b", { className: TONE_TEXT[faint ? 'none' : dotTone(v)], children: num2(v) }), ' ', _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: intRu(g.purchases) })] }));
}
/**
 * Колонки для сортировки: `value` — подпись значения, `all` — «Все кабинеты»,
 * `acc:<id>` — кабинет; `n` (без шапки) — покупок всего, порядок по умолчанию.
 * Числовые колонки сортируются по «вернётся на 1 $».
 */
/** Сколько отчётов по кабинетам идут одновременно. */
export const BY_ACCOUNTS_PARALLEL = 2;
const DEFAULT_SORT = { key: 'n', dir: 'desc' };
const accKey = (id) => `acc:${id}`;
/**
 * Колонки таблицы для меню «Колонки»: «Значение» (закреплено, всегда видно),
 * «Все кабинеты» и по колонке на кабинет (`acc:<id>`, подпись — название
 * кабинета). Служебная `n` (порядок по умолчанию) сюда не входит: у неё нет
 * шапки. Кабинет, убранный из выбора, хранит свои настройки и вернётся с ними.
 */
export function byAccountsColumnDefs(accounts) {
    return [
        { id: 'value', label: 'Значение', defaultPin: 'left', hideable: false },
        { id: 'all', label: 'Все кабинеты' },
        ...accounts.map((a) => ({ id: accKey(a.id), label: a.title })),
    ];
}
/** Значение строки для колонки `key` (чистая функция — под тест). */
export function byAccountsSortValue(r, key) {
    if (key === 'value')
        return r.label;
    if (key === 'n')
        return r.total?.purchases ?? null;
    if (key === 'all')
        return r.total?.answer.roas3 ?? null;
    if (key.startsWith('acc:'))
        return r.byAccount.get(key.slice(4))?.answer.roas3 ?? null;
    return null;
}
/**
 * «Признак × кабинеты» (спека §6.4): тот же признак по каждому выбранному
 * кабинету. Отчёт по одному кабинету на колонку — ленивые запросы только в
 * этом режиме, кэш общий с остальными экранами по ключу. Без `keepPreviousData`:
 * колонка кабинета не должна показывать числа другого кабинета. Сортировка по
 * любой колонке и пагинация — в адресе (`x*`), как у остальных таблиц.
 */
export function MarkerByAccounts({ state, report, markerKey, }) {
    const ids = state.url.accounts.all
        ? state.accountRows.map((a) => a.platformAccountId)
        : state.url.accounts.ids;
    const table = useTableUrl('x', ['n', 'value', 'all', ...ids.map(accKey)], DEFAULT_SORT);
    // Отчёты по кабинетам — не больше BY_ACCOUNTS_PARALLEL одновременно: i-й
    // стартует, когда (i − 2)-й уже ответил. 15–20 кабинетов иначе разом
    // занимают очередь отчётов admin-api (3 слота) и соседние экраны ждут.
    const qc = useQueryClient();
    const queries = ids.map((id) => {
        const q = { ...state.reportQuery, accounts: [id] };
        return { q, key: reportKey(q) };
    });
    const results = useQueries({
        queries: queries.map(({ q, key }, i) => {
            const before = queries[i - BY_ACCOUNTS_PARALLEL];
            const status = before ? qc.getQueryState(before.key)?.status : undefined;
            // Нет предшественника — сразу; есть — когда он ответил (данные или ошибка).
            const settled = !before || status === 'success' || status === 'error';
            return {
                queryKey: key,
                enabled: settled,
                queryFn: () => paybackApi.report(q),
                staleTime: PAYBACK_STALE_MS,
                retry: retryOnce,
                refetchOnWindowFocus: false,
            };
        }),
    });
    const total = markerValues(report.markers[markerKey]);
    const theta = thetaOf(report);
    const accounts = ids.map((id, i) => ({
        id,
        title: accountTitle(state.accountNames.get(id)),
        r: results[i],
        values: markerValues(results[i]?.data?.markers[markerKey]),
    }));
    const byId = new Map(accounts.map((a) => [accKey(a.id), a]));
    // Описание колонок стабильно, пока не сменился набор кабинетов или их названия.
    const titlesSig = JSON.stringify(accounts.map((a) => [a.id, a.title]));
    const defs = useMemo(() => byAccountsColumnDefs(JSON.parse(titlesSig).map(([id, title]) => ({ id, title }))), [titlesSig]);
    const cols = useTableColumns(paybackColumnsKey('by-accounts'), defs);
    const rows = total
        ? Object.keys(total).map((raw) => ({
            raw,
            label: markerValueLabel(markerKey, raw),
            total: total[raw],
            byAccount: new Map(accounts.map((c) => [c.id, c.values?.[raw]])),
        }))
        : [];
    const sorted = sortBy(rows, table.sort, byAccountsSortValue);
    const paged = paginate(sorted, table.page, table.size);
    return (_jsxs("div", { className: "flex flex-col gap-2.5", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "flex-1" }), _jsx(TableColumnsMenu, { columns: cols, sort: menuSort(table.sort, defs) })] }), _jsxs(Card, { children: [_jsxs(Table, { density: "compact", stickyHeader: true, "data-testid": "marker-by-accounts", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((col) => {
                                        const isValue = col.id === 'value';
                                        return (_jsx(SortableHeader, { column: col, sortKey: col.id, sort: table.sort, onSortChange: table.setSort, align: isValue ? 'left' : 'right', defaultDirection: isValue ? 'asc' : 'desc', className: "whitespace-nowrap", hint: isValue
                                                ? undefined
                                                : col.id === 'all'
                                                    ? columnHint('all', theta)
                                                    : hintOf('oneAccount', theta), children: col.def.label }, col.id));
                                    }) }) }), _jsx(Table.Body, { children: paged.rows.map((row) => (_jsx(Table.Row, { "data-testid": "by-accounts-row", children: cols.visible.map((col) => {
                                        if (col.id === 'value') {
                                            return (_jsx(Table.Cell, { column: col, children: row.label }, col.id));
                                        }
                                        if (col.id === 'all') {
                                            return (_jsx(Table.Cell, { column: col, className: "text-right tabular-nums", children: _jsx(ValueCell, { g: row.total }) }, col.id));
                                        }
                                        const c = byId.get(col.id);
                                        return (_jsx(Table.Cell, { column: col, className: "text-right tabular-nums", children: !c ? null : c.r?.isPending && !c.r.isError ? (_jsx(Skeleton, { className: "ml-auto h-4 w-12" })) : c.r?.isError ? (_jsx("span", { className: "text-xs text-[var(--color-state-danger-fg)]", children: "\u043E\u0448\u0438\u0431\u043A\u0430" })) : (_jsx(ValueCell, { g: row.byAccount.get(c.id) })) }, col.id));
                                    }) }, row.raw))) })] }), _jsxs("p", { className: "px-3 py-2 text-xs text-[var(--color-text-muted)]", children: ["\u0412 \u044F\u0447\u0435\u0439\u043A\u0435 \u2014 \u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0432\u0435\u0440\u043D\u0451\u0442\u0441\u044F \u043D\u0430 1 $ \u0438 (\u0441\u0435\u0440\u044B\u043C) \u043F\u043E\u043A\u0443\u043F\u043E\u043A. \u0411\u043B\u0435\u0434\u043D\u043E \u2014 \u043C\u0435\u043D\u044C\u0448\u0435 ", MIN_N, " \u043F\u043E\u043A\u0443\u043F\u043E\u043A. \u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u043A\u0430 \u043F\u043E \u043A\u043E\u043B\u043E\u043D\u043A\u0435 \u2014 \u043F\u043E \u00AB\u0432\u0435\u0440\u043D\u0451\u0442\u0441\u044F \u043D\u0430 1 $\u00BB."] })] }), paged.total > table.size ? (_jsx(Pagination, { page: paged.page, pageSize: table.size, total: paged.total, onPageChange: table.setPage, onPageSizeChange: table.setSize })) : null] }));
}
//# sourceMappingURL=marker-by-accounts.js.map